/**
 * Accounting report · "Invoices" view — every QuickBooks invoice dated in the
 * window with sent / paid / owing / overdue status, plus the per-customer
 * breakdown. Data is the local QuickBooks sync cache (Sync QBO refreshes it).
 */
const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  paid: ["Paid", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  owing: ["Owing", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  overdue: ["Overdue", "bg-red-500/15 text-red-300 border-red-500/30"],
};

export default function InvoicesSentView({ data, loading }) {
  const inv = data.invoices || {};
  const rows = data.invoice_rows || [];
  return (
    <div className={`space-y-4 ${loading ? "opacity-60" : ""}`} data-testid="accounting-invoices-view">
      {!inv.connected && (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-mono text-[10px] text-amber-200" data-testid="accounting-invoices-not-connected">
          QuickBooks isn't connected — these are the invoices from the last sync{inv.synced_at ? ` (${inv.synced_at.slice(0, 16).replace("T", " ")})` : ""}. Connect it in Settings → API, then tap Sync QBO.
        </p>
      )}

      {inv.customers?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="accounting-invoices-customers">
          <table className="w-full min-w-[480px] border-collapse text-xs">
            <thead>
              <tr className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                <th className="px-3 py-2 text-left font-normal">Customer</th>
                <th className="px-3 py-2 text-right font-normal">Invoices</th>
                <th className="px-3 py-2 text-right font-normal">Sent</th>
                <th className="px-3 py-2 text-right font-normal">Owing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {inv.customers.map((c) => (
                <tr key={c.customer} className="font-mono tabular-nums text-white/80">
                  <td className="px-3 py-1.5 font-sans font-semibold text-white">{c.customer}</td>
                  <td className="px-3 py-1.5 text-right">{c.count}</td>
                  <td className="px-3 py-1.5 text-right">{money(c.total)}</td>
                  <td className={`px-3 py-1.5 text-right ${c.owing > 0 ? "text-amber-300" : "text-emerald-300"}`}>{money(c.owing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="accounting-invoices-table">
        <table className="w-full min-w-[820px] border-collapse text-xs">
          <thead>
            <tr className="font-mono text-[9px] uppercase tracking-widest text-white/50">
              <th className="px-3 py-2 text-left font-normal">Invoice #</th>
              <th className="px-3 py-2 text-left font-normal">Customer</th>
              <th className="px-3 py-2 text-left font-normal">PO</th>
              <th className="px-3 py-2 text-left font-normal">Date</th>
              <th className="px-3 py-2 text-left font-normal">Due</th>
              <th className="px-3 py-2 text-left font-normal">Status</th>
              <th className="px-3 py-2 text-right font-normal">Total</th>
              <th className="px-3 py-2 text-right font-normal">Paid</th>
              <th className="px-3 py-2 text-right font-normal">Owing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center font-mono text-[11px] text-white/50">No QuickBooks invoices in this window.</td></tr>
            ) : rows.map((r) => {
              const [lb, cls] = STATUS[r.status] || STATUS.owing;
              return (
                <tr key={r.qbo_id} className="font-mono tabular-nums text-white/80 hover:bg-white/[0.03]" data-testid={`accounting-invoice-${r.doc_number || r.qbo_id}`}>
                  <td className="px-3 py-1.5 font-semibold text-white">{r.doc_number || "—"}</td>
                  <td className="px-3 py-1.5 font-sans">{r.customer || "—"}</td>
                  <td className="px-3 py-1.5 font-sans text-white/60">{r.po || "—"}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.due_date || "—"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${cls}`}>
                      {lb}{r.status === "overdue" ? ` · ${r.days_overdue}d` : ""}{r.status === "paid" && r.paid_date ? ` · ${r.paid_date}` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">{money(r.total)}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-300">{money(r.paid)}</td>
                  <td className={`px-3 py-1.5 text-right ${r.balance > 0 ? "text-amber-300" : "text-white/40"}`}>{money(r.balance)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/20 font-mono text-xs font-bold tabular-nums text-white">
              <td className="px-3 py-2 font-sans" colSpan={6}>Total · {inv.count || 0} invoice{inv.count === 1 ? "" : "s"} · {inv.overdue_count || 0} overdue</td>
              <td className="px-3 py-2 text-right">{money(inv.sent_total)}</td>
              <td className="px-3 py-2 text-right text-emerald-300">{money(inv.paid_total)}</td>
              <td className="px-3 py-2 text-right text-amber-300">{money(inv.owing_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
